import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { locales, type Locale } from '@holymoly/i18n';
import { PageTitle } from '@/components/PageTitle';
import { Field } from '@/components/ui/Field';
import { ConfirmDialog, Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toaster';
import { errorText } from '@/lib/errors';
import { formatDate, formatDateTime } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { client, orpc } from '@/lib/orpc';
import { useCan, useMe } from '@/lib/session';

export const Route = createFileRoute('/_app/admin/users')({
  beforeLoad: ({ context }) => requirePermission(context.me, 'users.view'),
  component: UsersPage
});

type User = Awaited<ReturnType<typeof client.users.list>>[number];
type Pending = { kind: 'deactivate' | 'reset2fa'; user: User } | null;

function UsersPage() {
  const { t } = useTranslation();
  const can = useCan();
  const me = useMe();
  const toast = useToast();
  const queryClient = useQueryClient();
  const users = useQuery(orpc.users.list.queryOptions());
  const invites = useQuery(orpc.invites.list.queryOptions());
  const roles = useQuery(orpc.roles.list.queryOptions());
  const manage = can('users.manage');
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [manualLink, setManualLink] = useState<string | null>(null);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.users.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.invites.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.roles.key() })
    ]);
  const onError = (e: unknown) => toast('error', errorText(t, e));

  const act = useMutation({
    mutationFn: async (p: NonNullable<Pending>) =>
      p.kind === 'deactivate' ? client.users.deactivate({ id: p.user.id }) : client.users.resetTwoFactor({ id: p.user.id }),
    onSuccess: async () => {
      setPending(null);
      await refresh();
    },
    onError
  });
  const reactivate = useMutation({ mutationFn: (id: string) => client.users.reactivate({ id }), onSuccess: refresh, onError });
  const resend = useMutation({
    mutationFn: (id: string) => client.invites.resend({ id }),
    onSuccess: (res) => (res.inviteUrl ? setManualLink(res.inviteUrl) : toast('success', t('admin.resent'))),
    onError
  });
  const revokeInvite = useMutation({ mutationFn: (id: string) => client.invites.revoke({ id }), onSuccess: refresh, onError });

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('admin.users')}
        items={[{ label: t('admin.section') }, { label: t('admin.users'), active: true }]}
        actions={
          manage && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setInviting(true)}>
              <span className="iconify lucide--user-plus size-4" />
              {t('admin.invite')}
            </button>
          )
        }
      />

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('admin.name')}</th>
                  <th>{t('admin.rolesCol')}</th>
                  <th>{t('admin.twoFactorShort')}</th>
                  <th>{t('admin.lastSeen')}</th>
                  <th>{t('admin.status')}</th>
                  {manage && <th />}
                </tr>
              </thead>
              <tbody>
                {users.isPending && (
                  <tr>
                    <td colSpan={6}>
                      <div className="skeleton h-8 w-full" />
                    </td>
                  </tr>
                )}
                {users.data?.map((u) => (
                  <tr key={u.id} className={u.deactivatedAt ? 'opacity-60' : ''}>
                    <td>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-base-content/60 text-xs">{u.email}</p>
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {u.roles.length === 0 && <span className="text-base-content/50 text-sm">{t('admin.noRoles')}</span>}
                        {u.roles.map((r) => (
                          <span key={r.id} className="badge badge-soft badge-primary badge-sm">
                            {r.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={`iconify size-4 ${u.twoFactorEnabled ? 'lucide--shield-check text-success' : 'lucide--shield-off text-base-content/40'}`} />
                    </td>
                    <td className="text-sm">{formatDateTime(u.lastSeenAt)}</td>
                    <td>
                      <span className={`badge badge-sm ${u.deactivatedAt ? 'badge-ghost' : 'badge-success badge-soft'}`}>
                        {u.deactivatedAt ? t('admin.deactivated') : t('admin.active')}
                      </span>
                    </td>
                    {manage && (
                      <td className="text-end">
                        <div className="dropdown dropdown-end">
                          <button type="button" className="btn btn-ghost btn-sm btn-square" aria-label={t('admin.editRoles')}>
                            <span className="iconify lucide--ellipsis size-4" />
                          </button>
                          <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-box z-10 w-52 p-2 shadow-sm">
                            <li>
                              <button type="button" onClick={() => setEditing(u)}>
                                <span className="iconify lucide--shield size-4" />
                                {t('admin.editRoles')}
                              </button>
                            </li>
                            {u.twoFactorEnabled && (
                              <li>
                                <button type="button" onClick={() => setPending({ kind: 'reset2fa', user: u })}>
                                  <span className="iconify lucide--rotate-ccw size-4" />
                                  {t('admin.resetTwoFactor')}
                                </button>
                              </li>
                            )}
                            {u.id !== me.user.id &&
                              (u.deactivatedAt ? (
                                <li>
                                  <button type="button" onClick={() => reactivate.mutate(u.id)}>
                                    <span className="iconify lucide--user-check size-4" />
                                    {t('admin.reactivate')}
                                  </button>
                                </li>
                              ) : (
                                <li>
                                  <button type="button" className="text-error" onClick={() => setPending({ kind: 'deactivate', user: u })}>
                                    <span className="iconify lucide--user-x size-4" />
                                    {t('admin.deactivate')}
                                  </button>
                                </li>
                              ))}
                          </ul>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {invites.data && invites.data.length > 0 && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body">
            <h2 className="card-title text-base">{t('admin.openInvites')}</h2>
            <ul className="divide-base-300 mt-2 divide-y">
              {invites.data.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                  <div>
                    <p className="font-medium">{i.email}</p>
                    <p className="text-base-content/60 text-xs">
                      {i.roles.map((r) => r.name).join(', ') || t('admin.noRoles')} ·{' '}
                      {i.expired ? t('admin.inviteExpired') : t('admin.expiresOn', { date: formatDate(i.expiresAt) })}
                    </p>
                  </div>
                  {manage && (
                    <div className="flex gap-2">
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => resend.mutate(i.id)}>
                        {t('admin.resend')}
                      </button>
                      <button type="button" className="btn btn-sm btn-ghost text-error" onClick={() => revokeInvite.mutate(i.id)}>
                        {t('admin.revokeInvite')}
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <InviteModal
        open={inviting}
        roles={roles.data ?? []}
        onClose={() => setInviting(false)}
        onDone={async (email, url) => {
          setInviting(false);
          if (url) setManualLink(url);
          else toast('success', t('admin.inviteSent', { email }));
          await refresh();
        }}
      />
      {editing && (
        <RolesModal
          user={editing}
          roles={roles.data ?? []}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
      <ConfirmDialog
        open={pending !== null}
        danger={pending?.kind === 'deactivate'}
        busy={act.isPending}
        text={
          pending &&
          (pending.kind === 'deactivate'
            ? t('admin.confirmDeactivate', { name: pending.user.name })
            : t('admin.confirmResetTwoFactor', { name: pending.user.name }))
        }
        onClose={() => setPending(null)}
        onConfirm={() => act.mutate(pending!)}
      />
      <Modal open={manualLink !== null} onClose={() => setManualLink(null)} title={t('admin.inviteTitle')}>
        <p className="text-sm">{t('admin.inviteLinkManual')}</p>
        <code className="bg-base-200 rounded-box mt-3 block p-3 font-mono text-xs break-all">{manualLink}</code>
      </Modal>
    </div>
  );
}

type Role = Awaited<ReturnType<typeof client.roles.list>>[number];

function RoleChecklist({ roles, selected, onChange }: { roles: Role[]; selected: string[]; onChange: (ids: string[]) => void }) {
  return (
    <div className="space-y-2">
      {roles.map((r) => (
        <label key={r.id} className="border-base-300 rounded-box flex cursor-pointer items-start gap-3 border p-3">
          <input
            type="checkbox"
            className="checkbox checkbox-sm mt-0.5"
            checked={selected.includes(r.id)}
            onChange={(e) => onChange(e.target.checked ? [...selected, r.id] : selected.filter((x) => x !== r.id))}
          />
          <span>
            <span className="font-medium">{r.name}</span>
            {r.requireTwoFactor && <span className="iconify lucide--shield-check text-base-content/50 ms-1 size-3.5" />}
            <span className="text-base-content/60 block text-xs">{r.description}</span>
          </span>
        </label>
      ))}
    </div>
  );
}

function InviteModal({
  open,
  roles,
  onClose,
  onDone
}: {
  open: boolean;
  roles: Role[];
  onClose: () => void;
  onDone: (email: string, url: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [locale, setLocale] = useState<Locale>((i18n.resolvedLanguage as Locale) ?? 'de');
  const invite = useMutation({
    mutationFn: () => client.invites.create({ email, roleIds, locale }),
    onSuccess: (res) => {
      onDone(res.invite.email, res.inviteUrl);
      setEmail('');
      setRoleIds([]);
    },
    onError: (e) => toast('error', errorText(t, e))
  });

  return (
    <Modal open={open} onClose={onClose} title={t('admin.inviteTitle')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}>
        <Field label={t('auth.email')}>
          {(id) => <input id={id} className="input w-full" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />}
        </Field>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">{t('admin.rolesCol')}</legend>
          <RoleChecklist roles={roles} selected={roleIds} onChange={setRoleIds} />
        </fieldset>
        <Field label={t('admin.inviteLanguage')}>
          {(id) => (
            <select id={id} className="select w-full" value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
              {locales.map((l) => (
                <option key={l} value={l}>
                  {t(`languages.${l}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <button type="submit" className="btn btn-primary mt-4 w-full" disabled={invite.isPending}>
          {invite.isPending && <span className="loading loading-spinner loading-sm" />}
          {t('admin.invite')}
        </button>
      </form>
    </Modal>
  );
}

function RolesModal({ user, roles, onClose, onDone }: { user: User; roles: Role[]; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [selected, setSelected] = useState(user.roles.map((r) => r.id));
  const save = useMutation({
    mutationFn: () => client.users.setRoles({ id: user.id, roleIds: selected }),
    onSuccess: onDone,
    onError: (e) => toast('error', errorText(t, e))
  });
  return (
    <Modal
      open
      onClose={onClose}
      title={`${t('admin.editRoles')}: ${user.name}`}
      actions={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('admin.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {t('account.save')}
          </button>
        </>
      }>
      <RoleChecklist roles={roles} selected={selected} onChange={setSelected} />
    </Modal>
  );
}
